import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { useAction, useRueckfrage } from "../../components/ui";

/**
 * Basiszinssätze je Land (0055, Befund Einstellungen 15): der Betrieb trägt die halbjährlichen Sätze ein (Deutschland:
 * Bundesbank, § 247 BGB; Österreich: OeNB). Sie gelten sofort für alle Agenturen dieses Landes. Aufschlag und fester
 * Verzugszins der Schweiz stehen je Land in platform_interest_rules.
 */

interface Rate { country: string; valid_from: string; rate_bp: number; note: string | null }
interface Rule { country: string; mode: "base_plus" | "fixed"; surcharge_bp: number; fixed_bp: number | null; legal_basis: string | null }

const pct = (bp: number) => `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(bp / 100)} %`;
const dmy = (iso: string) => iso.slice(0, 10).split("-").reverse().join(".");

export function Basiszins() {
  const qc = useQueryClient();
  const rates = useQuery({ queryKey: ["platform_base_rates"], queryFn: () => api.select<Rate>("platform_base_rates", { order: [["country", "asc"], ["valid_from", "desc"]] }) });
  const rules = useQuery({ queryKey: ["platform_interest_rules"], queryFn: () => api.select<Rule>("platform_interest_rules", { order: [["country", "asc"]] }) });
  const { busy, error, attempt } = useAction();
  const rueckfrage = useRueckfrage();
  const [country, setCountry] = useState("DE");
  const [from, setFrom] = useState("");
  const [rate, setRate] = useState("");
  const [note, setNote] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const reload = () => { void qc.invalidateQueries({ queryKey: ["platform_base_rates"] }); };

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const n = Number(rate.trim().replace(",", ".").replace("%", ""));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setFehler("Bitte das Datum angeben, ab dem der Satz gilt.");
    if (!rate.trim() || !Number.isFinite(n) || n < -10 || n > 20) return setFehler("Bitte den Satz in Prozent eingeben, zum Beispiel 1,27 oder -0,88.");
    setFehler(null);
    void attempt("rate", () => api.rpc("platform_base_rate_set", { p_country: country, p_valid_from: from, p_rate_bp: Math.round(n * 100), p_note: note.trim() || null }),
      "Basiszins gespeichert. Er gilt für alle Agenturen dieses Landes.").then((ok) => { if (ok) { setFrom(""); setRate(""); setNote(""); reload(); } });
  }

  const byCountry = new Map<string, Rate[]>();
  for (const r of rates.data ?? []) byCountry.set(r.country, [...(byCountry.get(r.country) ?? []), r]);
  return (
    <section className="panel stack" aria-labelledby="basiszins-titel">
      {rueckfrage.element}
      <h2 id="basiszins-titel">Basiszins für Verzugszinsen</h2>
      <p>Die Agenturen schalten Verzugszinsen nur an oder aus; Basiszins und Aufschlag kommen von hier. Neue Sätze meist zum 1. Januar und 1. Juli.</p>
      <ul>
        {(rules.data ?? []).map((r) => (
          <li key={r.country}>{r.country}: {r.mode === "fixed" ? `fester Verzugszins ${pct(r.fixed_bp ?? 0)}` : `Basiszins plus ${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(r.surcharge_bp / 100)} Prozentpunkte`}{r.legal_basis ? ` · ${r.legal_basis}` : ""}</li>
        ))}
      </ul>
      {[...byCountry.entries()].map(([c, list]) => (
        <div key={c} className="tablewrap">
          <table>
            <caption>{c}</caption>
            <thead><tr><th scope="col">Gültig ab</th><th scope="col" className="money">Satz</th><th scope="col">Quelle</th><th scope="col"><span className="sr-only">Aktion</span></th></tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.valid_from}>
                  <td>{dmy(r.valid_from)}</td>
                  <td><span className="num">{pct(r.rate_bp)}</span></td>
                  <td>{r.note ?? ""}</td>
                  <td><button className="danger" disabled={!!busy} onClick={async () => {
                    // Rückfrage im Portal-Dialog statt window.confirm (Portal einheitlich)
                    if (!(await rueckfrage.ask({ title: "Satz entfernen?", text: `Satz ${c} ab ${dmy(r.valid_from)} entfernen? Zinsen rechnen dann mit dem vorherigen Satz.`, confirmLabel: "Entfernen", danger: true }))) return;
                    void attempt(`rm:${c}${r.valid_from}`, () => api.rpc("platform_base_rate_delete", { p_country: c, p_valid_from: r.valid_from }), "Satz entfernt.").then((ok) => ok && reload());
                  }}>Entfernen</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {/* Länder mit Regel „Basiszins plus“ ohne einen einzigen Satz (SICHT-OEFFENTLICH-BETRIEB-19): sichtbar statt verschwiegen */}
      {(rules.data ?? []).filter((r) => r.mode === "base_plus" && !byCountry.has(r.country)).map((r) => (
        <p key={r.country} className="notice warning">{r.country}: noch kein Basiszins hinterlegt. Ohne Satz rechnen Agenturen dieses Landes keine Verzugszinsen.</p>
      ))}
      <form className="stack" onSubmit={submit} noValidate>
        <div className="grid">
          <label>Land<select value={country} onChange={(e) => setCountry(e.target.value)}><option value="DE">Deutschland</option><option value="AT">Österreich</option></select></label>
          <label>Gültig ab<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label>Satz in Prozent<input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="1,27" /></label>
          <label>Quelle (optional)<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Deutsche Bundesbank" /></label>
        </div>
        {(fehler || error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
        <div className="row"><button type="submit" className="primary" disabled={!!busy}>Basiszins speichern</button></div>
      </form>
    </section>
  );
}
